/**
 * Loads PoR page visibility — workspace default or per-record layout.
 */
import { useEffect } from 'react';
import { usePorPageSettingsStore } from '../stores/prioritization/porPageSettingsStore';
import { useTenantStore } from '../stores/prioritization/tenantStore';
import type { PorPageKey } from '../lib/porPageSettingsService';

export function usePorPageVisibility(porId?: string | null) {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const fetchWorkspaceSettings = usePorPageSettingsStore((s) => s.fetchWorkspaceSettings);
  const fetchRecordSettings = usePorPageSettingsStore((s) => s.fetchRecordSettings);
  const setMode = usePorPageSettingsStore((s) => s.setMode);
  const loading = usePorPageSettingsStore((s) => s.loading);
  const workspaceSettings = usePorPageSettingsStore((s) => s.workspaceSettings);
  const recordSettings = usePorPageSettingsStore((s) => s.recordSettings);
  const isVisibleFn = usePorPageSettingsStore((s) => s.isVisible);
  const visibleSectionOrderFn = usePorPageSettingsStore((s) => s.visibleSectionOrder);
  const defaultLandingSegmentFn = usePorPageSettingsStore((s) => s.defaultLandingSegment);
  const layoutSourceFn = usePorPageSettingsStore((s) => s.layoutSource);

  useEffect(() => {
    if (!workspaceId) return;
    if (porId) {
      void fetchRecordSettings(porId, workspaceId);
      return;
    }
    setMode('workspace');
    void fetchWorkspaceSettings(workspaceId);
  }, [workspaceId, porId, fetchWorkspaceSettings, fetchRecordSettings, setMode]);

  const hasData = porId ? recordSettings.length > 0 : workspaceSettings.length > 0;

  return {
    loading: loading && !hasData,
    isVisible: (key: PorPageKey) => isVisibleFn(key),
    visibleSectionOrder: visibleSectionOrderFn(),
    defaultLandingSegment: defaultLandingSegmentFn(),
    layoutSource: layoutSourceFn(),
  };
}
